import type { FC, SyntheticEvent } from 'react';
import { Collapse, Typography } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { WORK_ORDER_UI } from '~/app/components/maintenance/work-orders/work-order-ui-colors';
import type { KanbanTicketReportView } from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/types';

export interface TicketReportSnippetProps {
  report: KanbanTicketReportView;
  compact?: boolean;
  /** When true, the report panel starts expanded (ticket detail visits). */
  defaultExpanded?: boolean;
}

function hasReportContent(report: KanbanTicketReportView): boolean {
  return Boolean(
    report.executionResult ||
    report.summary?.trim() ||
    report.technicianComment?.trim() ||
    report.workPerformed?.trim() ||
    report.recommendation?.trim() ||
    report.faultDescription?.trim() ||
    report.faultCategory?.trim() ||
    report.confirmedSerialNumber?.trim() ||
    report.confirmedAssetIdentifier?.trim() ||
    report.pauseDurationMinutes != null ||
    report.arrivedAt,
  );
}

export function ticketHasReport(
  report: KanbanTicketReportView | null | undefined,
): report is KanbanTicketReportView {
  return report != null && hasReportContent(report);
}

function stopCardSelect(event: SyntheticEvent) {
  event.stopPropagation();
}

function formatArrivedAt(
  arrivedAt: Date | null,
  locale: string,
): string | null {
  if (!arrivedAt || Number.isNaN(arrivedAt.getTime())) return null;
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(arrivedAt);
}

export const TicketReportSnippet: FC<TicketReportSnippetProps> = ({
  report,
  compact = false,
  defaultExpanded = false,
}) => {
  const { t, i18n } = useTranslation('maintenance');
  if (!hasReportContent(report)) return null;

  const resultLabel = report.executionResult
    ? t(`workOrders.executionResult.${report.executionResult}`, {
        defaultValue: report.executionResult,
      })
    : null;

  const rows: Array<{ label: string; value: string }> = [];
  if (report.executionResult) {
    rows.push({
      label: t('workOrders.kanban.report.executionResult'),
      value: resultLabel ?? report.executionResult,
    });
  }
  if (report.summary?.trim()) {
    rows.push({
      label: t('workOrders.kanban.report.summary'),
      value: report.summary.trim(),
    });
  }
  if (report.technicianComment?.trim()) {
    rows.push({
      label: t('workOrders.kanban.report.technicianComment'),
      value: report.technicianComment.trim(),
    });
  }
  if (!compact && report.workPerformed?.trim()) {
    rows.push({
      label: t('workOrders.kanban.report.workPerformed'),
      value: report.workPerformed.trim(),
    });
  }
  if (!compact && report.recommendation?.trim()) {
    rows.push({
      label: t('workOrders.kanban.report.recommendation'),
      value: report.recommendation.trim(),
    });
  }
  if (!compact && report.confirmedSerialNumber?.trim()) {
    rows.push({
      label: t('workOrders.kanban.report.confirmedSerialNumber'),
      value: report.confirmedSerialNumber.trim(),
    });
  }
  if (!compact && report.confirmedAssetIdentifier?.trim()) {
    rows.push({
      label: t('workOrders.kanban.report.confirmedAssetIdentifier'),
      value: report.confirmedAssetIdentifier.trim(),
    });
  }
  if (!compact && report.pauseDurationMinutes != null) {
    rows.push({
      label: t('workOrders.kanban.report.pauseDurationMinutes'),
      value: String(report.pauseDurationMinutes),
    });
  }
  if (!compact) {
    const arrivedLabel = formatArrivedAt(report.arrivedAt, i18n.language);
    if (arrivedLabel) {
      rows.push({
        label: t('workOrders.kanban.report.arrivedAt'),
        value: arrivedLabel,
      });
    }
  }
  if (report.faultCategory?.trim() || report.faultDescription?.trim()) {
    rows.push({
      label: t('workOrders.kanban.report.fault'),
      value: [report.faultCategory, report.faultDescription]
        .filter((part) => part?.trim())
        .join(' — '),
    });
  }

  if (rows.length === 0) return null;

  return (
    <div
      onClick={stopCardSelect}
      onKeyDown={stopCardSelect}
      role="presentation"
    >
      <Collapse
        ghost
        size="small"
        style={{
          marginTop: compact ? 4 : 6,
          background: '#f8fafc',
          borderRadius: 8,
          border: `1px solid ${WORK_ORDER_UI.cardBorder}`,
        }}
        defaultActiveKey={defaultExpanded ? ['report'] : []}
        items={[
          {
            key: 'report',
            label: (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  flexWrap: 'wrap',
                  fontSize: compact ? 11 : 12,
                  fontWeight: 600,
                  color: WORK_ORDER_UI.sidebarNavy,
                }}
              >
                <FileTextOutlined />
                {t('workOrders.kanban.report.title')}
                {resultLabel ? (
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: compact ? 10 : 11, fontWeight: 500 }}
                  >
                    · {resultLabel}
                  </Typography.Text>
                ) : null}
              </span>
            ),
            children: (
              <div style={{ paddingBottom: 4 }}>
                {rows.map((row) => (
                  <div key={row.label} style={{ marginTop: 4 }}>
                    <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                      {row.label}
                    </Typography.Text>
                    <Typography.Paragraph
                      ellipsis={compact ? { rows: 3 } : undefined}
                      style={{
                        margin: 0,
                        fontSize: compact ? 11 : 12,
                        color: WORK_ORDER_UI.sidebarNavy,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {row.value}
                    </Typography.Paragraph>
                  </div>
                ))}
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};
